import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { faqService } from '../../services/api';

const FAQList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  const selectedCategory = searchParams.get('category');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await faqService.getCategories();
        setCategories(response.data.data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchFaqs = async () => {
      setLoading(true);
      try {
        const params = selectedCategory ? { categoryId: selectedCategory } : { isFeatured: true };
        const response = await faqService.getAll(params);
        setFaqs(response.data.data);
      } catch (error) {
        console.error('Failed to fetch FAQs:', error);
      } finally {
        setLoading(false);
      }
    };
    if (!searchResults) {
      fetchFaqs();
    }
  }, [selectedCategory, searchResults]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    setLoading(true);
    try {
      const response = await faqService.search(searchQuery);
      setSearchResults(response.data.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Knowledge Base</h1>
        <p className="text-gray-600">Find answers to frequently asked questions</p>
      </div>

      {/* Search */}
      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <input
                type="text"
                placeholder="Search for answers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10 pr-20"
              />
              <svg
                className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <button
                type="submit"
                className="absolute right-2 top-1.5 btn-primary btn-sm"
              >
                Search
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Search Results */}
      {searchResults && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="text-lg font-medium">
              Search Results ({searchResults.length})
            </h2>
            <button onClick={clearSearch} className="text-sm text-primary-600 hover:text-primary-700">
              Clear search
            </button>
          </div>
          <div className="divide-y divide-gray-200">
            {searchResults.length > 0 ? (
              searchResults.map((faq) => (
                <Link
                  key={faq.id}
                  to={`/faq/${faq.id}`}
                  className="block p-4 hover:bg-gray-50 transition-colors"
                >
                  <h3 className="font-medium text-gray-900">{faq.question}</h3>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                    {faq.answer.substring(0, 150)}...
                  </p>
                  <span className="text-xs text-primary-600 mt-2 inline-block">
                    {faq.category_name}
                  </span>
                </Link>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                <p>No results found for "{searchQuery}"</p>
                <p className="mt-2 text-sm">Try different keywords or browse categories below</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Categories and FAQs */}
      {!searchResults && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Categories Sidebar */}
          <div className="lg:col-span-1">
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-medium">Categories</h2>
              </div>
              <div className="divide-y divide-gray-200">
                <button
                  onClick={() => setSearchParams({})}
                  className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors ${
                    !selectedCategory ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'
                  }`}
                >
                  Featured FAQs
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSearchParams({ category: category.id })}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between ${
                      selectedCategory === category.id ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    <span>{category.name}</span>
                    <span className="text-gray-400">({category.faq_count})</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* FAQ List */}
          <div className="lg:col-span-3">
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-medium">
                  {selectedCategory
                    ? categories.find(c => c.id === selectedCategory)?.name || 'FAQs'
                    : 'Featured FAQs'}
                </h2>
              </div>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="spinner h-8 w-8"></div>
                </div>
              ) : faqs.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {faqs.map((faq) => (
                    <Link
                      key={faq.id}
                      to={`/faq/${faq.id}`}
                      className="block p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start">
                        <div className="flex-shrink-0 mt-0.5">
                          <svg className="h-5 w-5 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="ml-3 flex-1">
                          <h3 className="font-medium text-gray-900">{faq.question}</h3>
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {faq.answer.substring(0, 150)}...
                          </p>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-400">
                            <span>{faq.view_count} views</span>
                            <span>{faq.category_name}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <p>No FAQs found in this category</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Can't find answer */}
      <div className="card bg-primary-50 border-primary-200">
        <div className="card-body text-center">
          <h3 className="font-medium text-primary-900">Can't find what you're looking for?</h3>
          <p className="text-sm text-primary-700 mt-1">
            Submit a support ticket and our team will help you.
          </p>
          <Link to="/tickets/new" className="btn-primary btn-sm mt-4 inline-flex">
            Create Ticket
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FAQList;
